import React, { useState, useEffect } from "react";
import axios from "axios";
import { DataGrid, GridColDef, GridRowId } from "@mui/x-data-grid";
import {
  Box,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Alert,
  Typography,
} from "@mui/material";
import { Student } from "@/Types/Student";
import { Group } from "@/Types/Group";
import apiAxiosInstance from "../../utils/axiosInstance";
import { useTeacher } from "../../context/TeacherContext";

interface StudentListProps {
  onEdit?: (studentId: string) => void;
  onDelete?: (studentIds: string[]) => void;
  selectedIds?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
}

const StudentList: React.FC<StudentListProps> = ({ 
  onEdit, 
  onDelete, 
  selectedIds: externalSelectedIds,
  onSelectionChange 
}) => {
  const { teacher } = useTeacher();
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<Student[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<string[]>(externalSelectedIds || []);
  const [pageSize, setPageSize] = useState<number>(10);

  // Sync external selectedIds if provided
  useEffect(() => {
    if (externalSelectedIds !== undefined) {
      setSelectedIds(externalSelectedIds);
    }
  }, [externalSelectedIds]);

  const fetchStudents = async () => {
    if (!teacher?._id) {
      setError("No teacher ID found");
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log("Fetching students for teacher:", teacher._id);
      // Explicitly pass teacherId to guarantee full roster is returned
      const response = await apiAxiosInstance.get(`/api/students`, {
        params: { teacherId: teacher._id }
      });
      console.log("Students response:", response.data);

      let fetched: Student[] = response.data || [];

      // Fallback: if students array is empty, derive from groups (populated on backend)
      if (!fetched.length) {
        try {
          const groupsResp = await apiAxiosInstance.get(`/api/groups`);
          const groupsData = groupsResp.data || [];
          const map: Record<string, Student & { groupIds?: string[] }> = {};
          groupsData.forEach((g: any) => {
            (g.students || []).forEach((s: Student) => {
              if (!map[s._id]) map[s._id] = { ...s, groupIds: [] } as any;
              map[s._id].groupIds = Array.from(new Set([...(map[s._id].groupIds || []), g._id]));
            });
          });
          fetched = Object.values(map);
          console.log("Derived students from groups:", fetched.length);
        } catch (e) {
          console.warn("Failed deriving students from groups fallback", e);
        }
      }

      setStudents(fetched);
      setSelectedStudents(fetched); // Initialize with all students
    } catch (error) {
      console.error("Error fetching students:", error);
      setError("Failed to load students. Please try again.");
      // Set empty arrays as fallback
      setStudents([]);
      setSelectedStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    if (!teacher?._id) return;

    try {
      const response = await apiAxiosInstance.get("/api/groups");
      setGroups(response.data || []);
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchGroups();
  }, [teacher?._id]);

  // Debug effect to log state changes
  useEffect(() => {
    console.log("Students state changed:", students);
    console.log("Selected students state changed:", selectedStudents);
  }, [students, selectedStudents]);

  const filterStudents = () => {
    let filtered = [...students];

    if (selectedGroup && selectedGroup !== "0") {
      // Prefer filtering by actual group membership from groups state
      const selected = groups.find((g) => g._id === selectedGroup);
      if (selected) {
        const ids = new Set((selected.students as any[] || []).map((s: any) => (typeof s === 'string' ? s : s._id)));
        filtered = filtered.filter((student) => ids.has(student._id));
      } else {
        // Fallback to student.groupIds if groups not populated
        filtered = filtered.filter((student) =>
          Array.isArray(student.groupIds) && student.groupIds.includes(selectedGroup)
        );
      }
    }

    setSelectedStudents(filtered);
  };

  useEffect(() => {
    filterStudents();
  }, [selectedGroup, students]);

  const handleGroupChange = (event: SelectChangeEvent) => {
    setSelectedGroup(event.target.value);
  };

  const handleSelectionChange = (newSelection: string[]) => {
    setSelectedIds(newSelection);
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }
  };

  const columns: GridColDef[] = [
    { field: "firstName", headerName: "First Name", flex: 1, minWidth: 120 },
    { field: "lastName", headerName: "Last Name", flex: 1, minWidth: 120 },
    { field: "middleName", headerName: "Middle Name", flex: 1, minWidth: 120 },
    { field: "studentId", headerName: "Student ID", flex: 1, minWidth: 100 },
    { field: "gradeLevel", headerName: "Grade", flex: 0.75, minWidth: 80 },
    { field: "dateOfBirth", headerName: "Date of Birth", flex: 1, minWidth: 120 },
    { field: "parentEmail", headerName: "Parent Email", flex: 1.5, minWidth: 180 },
    { field: "parentPhone", headerName: "Parent Phone", flex: 1, minWidth: 130 }
  ];

  return (
    <Box sx={{ height: '100%', width: '100%', p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <FormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
        <InputLabel id="select-group-label">Select Group</InputLabel>
        <Select
          labelId="select-group-label"
          value={selectedGroup}
          onChange={handleGroupChange}
          label="Select Group"
        >
          <MenuItem value="0">All Students</MenuItem>
          {groups.map((group) => (
            <MenuItem key={group._id} value={group._id}>
              {group.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ height: 600, width: '100%' }}>
          {selectedStudents.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <Typography variant="h6" color="textSecondary">
                No students found. {students.length === 0 ? "Add some students to get started." : "Try selecting a different group."}
              </Typography>
            </Box>
          ) : (
            <DataGrid
              rows={selectedStudents}
              columns={columns}
              getRowId={(row: Student) => row._id}
              checkboxSelection
              disableSelectionOnClick
              selectionModel={selectedIds}
              onSelectionModelChange={(newSelectionModel) => {
                handleSelectionChange(newSelectionModel.map(String));
              }}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[5, 10, 20, 50]}
              pagination
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default StudentList;
